import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Star, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY = { name: "", location: "", loan_type: "", review: "", rating: 5, image: "", status: "active", sample: false };

export default function TestimonialsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing?.id) await api.put(`/admin/testimonials/${editing.id}`, { ...form, rating: Number(form.rating) });
      else await api.post("/admin/testimonials", { ...form, rating: Number(form.rating) });
      toast.success("Testimonial saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Testimonials</h1>
          <p className="text-sm text-brand-muted mt-0.5">Publish only verified customer reviews. Entries marked "sample" are development placeholders.</p>
        </div>
        <button onClick={() => { setEditing({}); setForm(EMPTY); }} data-testid="add-testimonial-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> Add Testimonial</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="testimonial-form">
          <input required className="input-site" placeholder="Customer Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" />
          <input className="input-site" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <input className="input-site" placeholder="Loan Type" value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })} />
          <div className="sm:col-span-3">
            <textarea required className="input-site min-h-[90px]" placeholder="Review *" value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} data-testid="testimonial-review" />
          </div>
          <select className="input-site" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}>
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} stars</option>)}
          </select>
          <select className="input-site" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Published</option><option value="inactive">Hidden</option>
          </select>
          <label className="flex items-center gap-2 text-xs font-semibold text-brand-muted">
            <input type="checkbox" className="w-4 h-4 accent-brand-red" checked={!!form.sample} onChange={(e) => setForm({ ...form, sample: e.target.checked })} /> Mark as sample/demo
          </label>
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="testimonial-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-heading font-bold text-sm text-brand-dark">{t.name}</p>
                {t.sample && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><FlaskConical size={10} /> Sample</span>}
              </div>
              <p className="text-xs text-brand-muted mt-0.5">{t.loan_type} · {t.location}</p>
              <div className="flex gap-0.5 text-brand-gold mt-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}</div>
              <p className="text-xs text-brand-dark/70 mt-3 leading-relaxed line-clamp-3">{t.review}</p>
              <div className="flex gap-3 mt-4 pt-3 border-t border-black/5">
                <button onClick={() => { setEditing(t); setForm(t); }} data-testid={`testimonial-edit-${t.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-dark hover:text-brand-red"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-muted hover:text-brand-red"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
